import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Fuel, 
  MapPin, 
  Phone, 
  ShieldCheck, 
  Zap, 
  Coffee, 
  Clock, 
  ChevronRight, 
  Search, 
  Menu, 
  X, 
  ArrowRight, 
  CheckCircle2, 
  Star, 
  Award, 
  Navigation,
  Sliders,
  Sparkles,
  Info,
  Car,
  Truck,
  Building2,
  ExternalLink,
  Percent,
  Flame,
  Droplets,
  Gauge,
  Briefcase,
  Users,
  UtensilsCrossed,
  Brush,
  ClipboardList,
  Shirt,
  ShoppingBasket,
  Beef,
  Apple,
  Carrot
} from "lucide-react";
import { 
  Lightning, 
  Shield, 
  GasPump, 
  MapPin as PhosphorMapPin,
  Clock as PhosphorClock
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

// High-end brand images curated from Unsplash
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1674521645695-815ef207866b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBwZXRyb2wlMjBzdGF0aW9uJTIwYXJjaGl0ZWN0dXJlJTIwbmVvbiUyMG5pZ2h0fGVufDB8fHx8MTc4NjY4NjEwNnww&ixlib=rb-4.1.0&q=85",
  forecourt: "https://images.unsplash.com/photo-1695561324569-5e47c76dc0a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBwZXRyb2wlMjBzdGF0aW9uJTIwYXJjaGl0ZWN0dXJlJTIwbmVvbiUyMG5pZ2h0fGVufDB8fHx8MTc4NjY4NjEwNnww&ixlib=rb-4.1.0&q=85",
  store: "https://images.unsplash.com/photo-1605282003441-a966bb348137?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBwZXRyb2wlMjBzdGF0aW9uJTIwYXJjaGl0ZWN0dXJlJTIwbmVvbiUyMG5pZ2h0fGVufDB8fHx8MTc4NjY4NjEwNnww&ixlib=rb-4.1.0&q=85",
  neon: "https://images.unsplash.com/photo-1635648551082-163db1e12530?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwZXRyb2wlMjBzdGF0aW9uJTIwYXJjaGl0ZWN0dXJlJTIwbmVvbiUyMG5pZ2h0fGVufDB8fHx8MTc4NjY4NjEwNnww&ixlib=rb-4.1.0&q=85",
  martInterior: "https://static.prod-images.emergentagent.com/jobs/e10e8a5a-56f0-4251-913e-a0e24e134040/images/92b23a557be51720a6abd00dceef57d0814b3a82fa881aa50d68b88759e01baf.jpeg"
};

const FUEL_PRICES = [
  { id: "speed99", name: "Speed 99 Octane", type: "Super Unleaded (RON 99)", price: "£1.68", unit: "/ litre", change: "+0.3%", color: "text-amber-400", badge: "Track Grade" },
  { id: "ultradiesel", name: "Ultra-Clean Diesel", type: "Low-Sulfur Euro VI", price: "£1.67", unit: "/ litre", change: "-0.4%", color: "text-cyan-400", badge: "Zero Ash" },
  { id: "premium", name: "Martand Gold Petrol", type: "Unleaded E10 · Multi-Additive", price: "£1.51", unit: "/ litre", change: "-0.2%", color: "text-rose-400", badge: "Bestseller" },
  { id: "ev", name: "Hyper-Charge DC 350kW", type: "Ultra-Fast EV Charging", price: "£0.79", unit: "/ kWh", change: "Stable", color: "text-emerald-400", badge: "15 min full" }
];

const STATIONS = [
  { 
    id: 1, 
    name: "Martand Flagship — Liscard Road", 
    address: "Liscard Road, Wallasey, CH44 9AD, United Kingdom", 
    status: "Open 24/7", 
    pumps: 16, 
    evChargers: 8, 
    amenities: ["Gourmet Mart", "EV Lounge", "Nitrogen Air", "Touchless Wash"],
    lat: "Wallasey", 
    lng: "CH44 9AD",
    distance: "Your local stop"
  },
  { 
    id: 2, 
    name: "Martand Metro Terminal - Downtown", 
    address: "Ring Road Interchange, Central Zone", 
    status: "Open 24/7", 
    pumps: 12, 
    evChargers: 6, 
    amenities: ["Artisan Cafe", "Quick Mart", "Tyre Clinic"],
    lat: "28.6139° N", 
    lng: "77.2090° E",
    distance: "3.5 km away"
  },
  { 
    id: 3, 
    name: "Martand Expressway Hub - North", 
    address: "Km Marker 42, Northern Arterialway", 
    status: "Open 24/7", 
    pumps: 20, 
    evChargers: 10, 
    amenities: ["Truck Rest Lounge", "Supercharge Bay", "Gourmet Diner", "24/7 Service"],
    lat: "28.7041° N", 
    lng: "77.1025° E",
    distance: "8.1 km away"
  }
];

const VACANCIES = [
  {
    id: "mart-supervisor",
    title: "Mart Supervisor",
    openings: 12,
    icon: ClipboardList,
    accent: "text-amber-400",
    accentBg: "bg-amber-500/10 border-amber-500/20",
    type: "Full-Time · Shift Based",
    description: "Lead daily mart operations — stock management, till supervision, staff rotas, and delivering a spotless customer experience."
  },
  {
    id: "cleaner",
    title: "Cleaner",
    openings: 6,
    icon: Brush,
    accent: "text-cyan-400",
    accentBg: "bg-cyan-500/10 border-cyan-500/20",
    type: "Full-Time / Part-Time",
    description: "Keep the forecourt, mart aisles, and customer facilities immaculate. Flexible morning and evening shifts available."
  },
  {
    id: "backend-officer",
    title: "Petrol Pump Backend Officer",
    openings: 8,
    icon: Fuel,
    accent: "text-rose-400",
    accentBg: "bg-rose-500/10 border-rose-500/20",
    type: "Full-Time · Rotational",
    description: "Manage fuel stock reconciliation, pump records, supplier deliveries, and back-office reporting for the forecourt."
  },
  {
    id: "butcher",
    title: "Butcher",
    openings: 7,
    icon: UtensilsCrossed,
    accent: "text-emerald-400",
    accentBg: "bg-emerald-500/10 border-emerald-500/20",
    type: "Full-Time · Fresh Counter",
    description: "Prepare and present quality fresh meat cuts at our mart counter. Food hygiene certification is a plus — training provided."
  }
];

const MART_CATEGORIES = [
  {
    id: "clothing",
    title: "Clothing",
    icon: Shirt,
    accent: "text-violet-400",
    accentBg: "bg-violet-500/10 border-violet-500/20",
    tag: "Everyday Wear",
    description: "Workwear, casual essentials, seasonal jackets, and accessories for the whole family — quality basics at forecourt-friendly prices."
  },
  {
    id: "grocery",
    title: "Grocery",
    icon: ShoppingBasket,
    accent: "text-amber-400",
    accentBg: "bg-amber-500/10 border-amber-500/20",
    tag: "Daily Essentials",
    description: "A full aisle of household staples — bread, milk, eggs, tinned goods, snacks, drinks, and world foods stocked fresh every day."
  },
  {
    id: "fresh-meat",
    title: "Fresh Meat",
    icon: Beef,
    accent: "text-rose-400",
    accentBg: "bg-rose-500/10 border-rose-500/20",
    tag: "Butcher Counter",
    description: "Quality cuts prepared daily at our in-store butcher counter — chicken, lamb, beef, and marinated specials, cut to order."
  },
  {
    id: "fresh-fruits",
    title: "Fresh Fruits",
    icon: Apple,
    accent: "text-emerald-400",
    accentBg: "bg-emerald-500/10 border-emerald-500/20",
    tag: "Daily Delivery",
    description: "Seasonal fruit delivered daily — apples, bananas, berries, citrus, and exotic picks, hand-checked for freshness."
  },
  {
    id: "fresh-vegetables",
    title: "Fresh Vegetables",
    icon: Carrot,
    accent: "text-lime-400",
    accentBg: "bg-lime-500/10 border-lime-500/20",
    tag: "Farm Fresh",
    description: "Crisp greens, root veg, salad boxes, and herbs — restocked every morning so your kitchen never runs short."
  },
  {
    id: "ready-to-eat",
    title: "Ready To Eat",
    icon: UtensilsCrossed,
    accent: "text-cyan-400",
    accentBg: "bg-cyan-500/10 border-cyan-500/20",
    tag: "Grab & Go",
    description: "Hot food counter, fresh sandwiches, wraps, meal deals, and artisan coffee — perfect for drivers on the move, 24/7."
  }
];

export default function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locatorModalOpen, setLocatorModalOpen] = useState(false);
  const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyForm, setApplyForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    position: VACANCIES[0].title,
    experience: "",
    message: ""
  });
  const [isApplying, setIsApplying] = useState(false);
  const [contactForm, setContactForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "General Enquiry",
    message: ""
  });
  const [isSendingContact, setIsSendingContact] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.fullName || !contactForm.email || !contactForm.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setIsSendingContact(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: contactForm.fullName,
          email: contactForm.email,
          phone: contactForm.phone,
          subject: contactForm.subject,
          message: contactForm.message,
        }),
      });
      if (!response.ok) throw new Error("Unable to send message");
      setIsSendingContact(false);
      toast.success("Message sent! We'll get back to you shortly.", {
        description: `A reply will be sent to ${contactForm.email}`
      });
      setContactForm({ fullName: "", email: "", phone: "", subject: "General Enquiry", message: "" });
    } catch (error) {
      setIsSendingContact(false);
      toast.error("We could not send your message. Please try again.");
    }
  };
  const [selectedCity, setSelectedCity] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Enquiry Form State
  const [formData, setFormData] = useState({
    fullName: "",
    company: "",
    email: "",
    phone: "",
    fleetSize: "10-50 vehicles",
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.phone) {
      toast.error("Please fill in all mandatory contact fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fleet-enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: formData.fullName,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          fleet_size: formData.fleetSize,
          notes: formData.notes,
        }),
      });
      if (!response.ok) throw new Error("Unable to submit enquiry");
      setIsSubmitting(false);
      setEnquiryModalOpen(false);
      toast.success("Fleet Enquiry Received! Our corporate fuel specialist will contact you within 2 hours.", {
        description: `Confirmation sent to ${formData.email}`
      });
      setFormData({
        fullName: "",
        company: "",
        email: "",
        phone: "",
        fleetSize: "10-50 vehicles",
        notes: ""
      });
    } catch (error) {
      setIsSubmitting(false);
      toast.error("We could not send your enquiry. Please try again.");
    }
  };

  const openApplyModal = (positionTitle) => {
    setApplyForm(prev => ({ ...prev, position: positionTitle || VACANCIES[0].title }));
    setApplyModalOpen(true);
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!applyForm.fullName || !applyForm.email || !applyForm.phone) {
      toast.error("Please fill in your name, email, and phone number.");
      return;
    }
    setIsApplying(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: applyForm.fullName,
          email: applyForm.email,
          phone: applyForm.phone,
          position: applyForm.position,
          experience: applyForm.experience,
          message: applyForm.message,
        }),
      });
      if (!response.ok) throw new Error("Unable to submit application");
      setIsApplying(false);
      setApplyModalOpen(false);
      toast.success(`Application received for ${applyForm.position}!`, {
        description: `Our hiring team will contact you at ${applyForm.email}`
      });
      setApplyForm({
        fullName: "",
        email: "",
        phone: "",
        position: VACANCIES[0].title,
        experience: "",
        message: ""
      });
    } catch (error) {
      setIsApplying(false);
      toast.error("We could not send your application. Please try again.");
    }
  };

  const filteredStations = STATIONS.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-[#F8F9FA] font-sans selection:bg-[#E63946] selection:text-white">
      <Toaster position="top-right" richColors />

      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0B0E]/80 backdrop-blur-xl border-b border-[#252836]">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="#" className="flex items-center gap-3 group" data-testid="brand-logo">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E63946] to-[#1D3557] flex items-center justify-center shadow-[0_0_20px_rgba(230,57,70,0.4)] group-hover:scale-105 transition-transform duration-300">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-extrabold tracking-wider text-xl bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
                MARTAND
              </span>
              <span className="block text-[10px] tracking-[0.25em] text-[#E63946] font-bold uppercase">
                PETROL & MOBILITY
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-5 text-[13px] font-medium text-slate-300 whitespace-nowrap">
            <a href="#fuels" className="hover:text-white transition-colors" data-testid="nav-fuels">Fuels & Quality</a>
            <a href="#forecourt" className="hover:text-white transition-colors" data-testid="nav-forecourt">Forecourt Hubs</a>
            <a href="#mart" className="hover:text-white transition-colors" data-testid="nav-mart">The Mart</a>
            <a href="#stations" className="hover:text-white transition-colors" data-testid="nav-stations">Station Finder</a>
            <a href="#sustainability" className="hover:text-white transition-colors" data-testid="nav-sustainability">Clean Energy</a>
            <a href="#fleet" className="hover:text-white transition-colors" data-testid="nav-fleet">Fleet Program</a>
            <a href="#careers" className="hover:text-white transition-colors" data-testid="nav-careers">Careers</a>
            <a href="#contact" className="hover:text-white transition-colors" data-testid="nav-contact">Contact</a>
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <button 
              onClick={() => setLocatorModalOpen(true)}
              className="px-4 py-2.5 rounded-lg bg-[#12141C] border border-[#252836] hover:border-[#E63946]/50 text-xs font-semibold tracking-wider uppercase flex items-center gap-2 transition-all hover:bg-[#1A1D29]"
              data-testid="header-locator-btn"
            >
              <Navigation className="w-3.5 h-3.5 text-[#E63946]" />
              <span>Find Station</span>
            </button>
            <button 
              onClick={() => setEnquiryModalOpen(true)}
              className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#E63946] to-[#c52833] hover:from-[#d32f3b] hover:to-[#b0222d] text-white text-xs font-bold tracking-wider uppercase shadow-[0_4px_20px_rgba(230,57,70,0.3)] transition-all transform hover:-translate-y-0.5"
              data-testid="header-fleet-btn"
            >
              Corporate Fleet
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-[#12141C] border border-[#252836] text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Nav Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-20 z-40 bg-[#12141C] border-b border-[#252836] p-6 md:hidden shadow-2xl"
          >
            <div className="flex flex-col gap-4">
              <a href="#fuels" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 py-2 border-b border-[#252836]" data-testid="mobile-nav-fuels">Fuels & Quality</a>
              <a href="#forecourt" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 py-2 border-b border-[#252836]" data-testid="mobile-nav-forecourt">Forecourt Hubs</a>
              <a href="#mart" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 py-2 border-b border-[#252836]" data-testid="mobile-nav-mart">The Mart</a>
              <a href="#stations" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 py-2 border-b border-[#252836]" data-testid="mobile-nav-stations">Station Finder</a>
              <a href="#sustainability" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 py-2 border-b border-[#252836]" data-testid="mobile-nav-sustainability">Clean Energy</a>
              <a href="#fleet" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 py-2 border-b border-[#252836]" data-testid="mobile-nav-fleet">Fleet Program</a>
              <a href="#careers" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 py-2 border-b border-[#252836]" data-testid="mobile-nav-careers">Careers</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 py-2" data-testid="mobile-nav-contact">Contact</a>
              
              <div className="flex gap-4 mt-4">
                <button data-testid="mobile-find-station-btn"
                  onClick={() => { setMobileMenuOpen(false); setLocatorModalOpen(true); }}
                  className="flex-1 py-3 rounded-lg bg-[#1A1D29] border border-[#252836] text-xs font-bold uppercase tracking-wider text-center"
                >
                  Find Station
                </button>
                <button data-testid="mobile-fleet-enquiry-btn"
                  onClick={() => { setMobileMenuOpen(false); setEnquiryModalOpen(true); }}
                  className="flex-1 py-3 rounded-lg bg-[#E63946] text-white text-xs font-bold uppercase tracking-wider text-center"
                >
                  Fleet Enquiry
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-24 md:pt-44 md:pb-36 overflow-hidden">
        {/* Background gradient & image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.hero} 
            alt="Martand Petrol Station at Night" 
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0E] via-[#0A0B0E]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-[#0A0B0E]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E63946]/10 border border-[#E63946]/30 text-[#E63946] text-xs font-bold tracking-[0.2em] uppercase">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Fuel, food, and everything between</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1]">
                YOUR EVERYDAY STOP. <br />
                <span className="bg-gradient-to-r from-[#E63946] via-[#ff6b6b] to-[#FFB703] bg-clip-text text-transparent">
                  MART & PETROL PUMP.
                </span>
              </h1>

              <p className="text-lg text-slate-300 max-w-2xl leading-relaxed">
                Welcome to Martand Petrol — your dependable Wallasey fuel stop and neighbourhood mart. Fill up, grab essentials, recharge, and get back on the road feeling ready.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button 
                  onClick={() => setLocatorModalOpen(true)}
                  className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#E63946] to-[#c52833] text-white font-bold text-sm tracking-wider uppercase shadow-[0_10px_30px_rgba(230,57,70,0.4)] hover:shadow-[0_15px_40px_rgba(230,57,70,0.6)] transition-all transform hover:-translate-y-1 flex items-center gap-3"
                  data-testid="hero-find-station-btn"
                >
                  <Navigation className="w-4 h-4" />
                  <span>Locate Nearest Station</span>
                </button>
                <a 
                  href="#fuels"
                  className="px-8 py-4 rounded-xl bg-[#12141C] border border-[#252836] hover:border-slate-500 text-slate-200 font-bold text-sm tracking-wider uppercase transition-all flex items-center gap-2 hover:bg-[#1A1D29]"
                  data-testid="hero-explore-fuels-btn"
                >
                  <span>Explore Fuel Octane</span>
                  <ChevronRight className="w-4 h-4 text-[#E63946]" />
                </a>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-[#252836]">
                <div>
                  <div className="text-2xl lg:text-3xl font-black text-white">99.9%</div>
                  <div className="text-xs text-slate-400 font-medium tracking-wider uppercase mt-1">Fuel Purity Tested</div>
                </div>
                <div>
                  <div className="text-2xl lg:text-3xl font-black text-[#E63946]">24/7</div>
                  <div className="text-xs text-slate-400 font-medium tracking-wider uppercase mt-1">Automated Forecourt</div>
                </div>
                <div>
                  <div className="text-2xl lg:text-3xl font-black text-white">100%</div>
                  <div className="text-xs text-slate-400 font-medium tracking-wider uppercase mt-1">Green Energy Offset</div>
                </div>
              </div>
            </div>

            {/* Live Pricing Ticker Card */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-[#12141C]/90 backdrop-blur-2xl border border-[#252836] p-6 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#E63946]/10 rounded-full blur-3xl pointer-events-none" />
                
                <div className="flex items-center justify-between pb-6 border-b border-[#252836]">
                  <div>
                    <h3 className="text-lg font-bold">Live Forecourt Ticker</h3>
                    <p className="text-xs text-slate-400">Real-time pricing across all tiers</p>
                  </div>
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    Live Feed
                  </span>
                </div>

                <div className="divide-y divide-[#252836] my-2">
                  {FUEL_PRICES.map((fuel) => (
                    <div key={fuel.id} className="py-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-white">{fuel.name}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-[#1A1D29] text-slate-300 font-semibold">{fuel.badge}</span>
                        </div>
                        <span className="text-xs text-slate-400">{fuel.type}</span>
                      </div>
                      <div className="text-right">
                        <div className={`font-mono font-bold text-base ${fuel.color}`}>{fuel.price}</div>
                        <span className="text-[10px] text-slate-500">{fuel.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-[#252836] flex items-center justify-between">
                  <span className="text-xs text-slate-400">Next update in 14:22 mins</span>
                  <button 
                    onClick={() => {
                      toast.success("Prices synchronized with National Oil Benchmark.");
                    }}
                    className="text-xs font-bold text-[#E63946] hover:text-white transition-colors flex items-center gap-1"
                    data-testid="refresh-prices-btn"
                  >
                    <span>Sync Rates</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FUELS & QUALITY MATRIX */}
      <section id="fuels" className="py-24 bg-[#0D0E14] border-t border-b border-[#252836]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold tracking-[0.2em] text-[#E63946] uppercase">Uncompromising Standards</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight">ENGINEERED FOR PEAK COMBUSTION</h2>
            <p className="text-slate-400 text-base">
              Every drop of Martand fuel undergoes 18 distinct laboratory purity checks before entering our underground double-walled cryogenic storage tanks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <div className="rounded-2xl bg-[#12141C] border border-[#252836] p-8 flex flex-col justify-between hover:border-[#E63946]/50 transition-all group" data-testid="fuel-card-1">
              <div>
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 mb-6 group-hover:scale-110 transition-transform">
                  <Flame className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Speed 99 Octane</h3>
                <p className="text-xs text-slate-400 leading-relaxed mb-6">
                  Designed for sports cars and high-compression engines. Delivers cleaner combustion, carbon deposit removal, and maximum torque.
                </p>
              </div>
              <div className="pt-4 border-t border-[#252836] flex items-center justify-between text-xs font-mono">
                <span className="text-slate-400">RON Rating</span>
                <span className="text-amber-400 font-bold">99.5 Min</span>
              </div>
            </div>

            <div className="rounded-2xl bg-[#12141C] border border-[#252836] p-8 flex flex-col justify-between hover:border-[#E63946]/50 transition-all group" data-testid="fuel-card-2">
              <div>
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 mb-6 group-hover:scale-110 transition-transform">
                  <Droplets className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Ultra-Clean Diesel</h3>
                <p className="text-xs text-slate-400 leading-relaxed mb-6">
                  Euro VI compliant with ultra-low sulfur content (&lt; 10 ppm). Engineered for heavy fleet longevity and lower particulate emissions.
                </p>
              </div>
              <div className="pt-4 border-t border-[#252836] flex items-center justify-between text-xs font-mono">
                <span className="text-slate-400">Sulfur Level</span>
                <span className="text-cyan-400 font-bold">&lt; 10 ppm</span>
              </div>
            </div>

            <div className="rounded-2xl bg-[#12141C] border border-[#252836] p-8 flex flex-col justify-between hover:border-[#E63946]/50 transition-all group" data-testid="fuel-card-3">
              <div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-6 group-hover:scale-110 transition-transform">
                  <Zap className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">350kW DC Hyper-Charge</h3>
                <p className="text-xs text-slate-400 leading-relaxed mb-6">
                  Lightning-fast EV charging powered entirely by solar and wind rooftop canopies. Zero friction payment via RFID or App.
                </p>
              </div>
              <div className="pt-4 border-t border-[#252836] flex items-center justify-between text-xs font-mono">
                <span className="text-slate-400">Charging Speed</span>
                <span className="text-emerald-400 font-bold">10-80% in 15m</span>
              </div>
            </div>

            <div className="rounded-2xl bg-[#12141C] border border-[#252836] p-8 flex flex-col justify-between hover:border-[#E63946]/50 transition-all group" data-testid="fuel-card-4">
              <div>
                <div className="w-12 h-12 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 mb-6 group-hover:scale-110 transition-transform">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Martand Gold Petrol</h3>
                <p className="text-xs text-slate-400 leading-relaxed mb-6">
                  Infused with friction-reducing frictionless polymer additives that protect fuel injectors and boost mileage by up to 4.2%.
                </p>
              </div>
              <div className="pt-4 border-t border-[#252836] flex items-center justify-between text-xs font-mono">
                <span className="text-slate-400">Efficiency Gain</span>
                <span className="text-rose-400 font-bold">+4.2% MPG</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FORECOURT & AMENITIES */}
      <section id="forecourt" className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            <div className="space-y-6">
              <span className="text-xs font-bold tracking-[0.2em] text-[#E63946] uppercase">Beyond The Pump</span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">THE ARCHITECTURAL FORECOURT</h2>
              <p className="text-slate-300 leading-relaxed">
                We discarded the traditional, cluttered gas station aesthetic. Martand forecourts feature sweeping architectural canopies, LED navigation light-strips, and premium roadside amenities designed for the modern driver.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                <div className="p-5 rounded-xl bg-[#12141C] border border-[#252836]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#E63946]/10 flex items-center justify-center text-[#E63946]">
                      <Coffee className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-base">Gourmet 24/7 Mart</h4>
                  </div>
                  <p className="text-xs text-slate-400">Artisan coffee, fresh organic snacks, and gourmet provisions on the go.</p>
                </div>

                <div className="p-5 rounded-xl bg-[#12141C] border border-[#252836]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400">
                      <Gauge className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-base">Automated Air & Tyre</h4>
                  </div>
                  <p className="text-xs text-slate-400">Digital nitro-fill stations with automatic pressure calibration for absolute safety.</p>
                </div>
              </div>

              <div className="pt-4">
                <button 
                  onClick={() => setLocatorModalOpen(true)}
                  className="px-6 py-3.5 rounded-xl bg-[#1A1D29] border border-[#252836] hover:border-[#E63946] text-white text-xs font-bold tracking-wider uppercase flex items-center gap-2 transition-all"
                >
                  <span>View All Station Amenities</span>
                  <ArrowRight className="w-4 h-4 text-[#E63946]" />
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#E63946]/20 to-[#1D3557]/20 rounded-3xl blur-2xl -z-10" />
              <div className="rounded-2xl overflow-hidden border border-[#252836] shadow-2xl bg-[#12141C]">
                <img 
                  src={IMAGES.forecourt} 
                  alt="Martand Forecourt at Night" 
                  className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="p-6 bg-[#12141C] border-t border-[#252836] flex items-center justify-between">
                  <div>
                    <h5 className="font-bold text-sm">Martand Flagship Aerocity Hub</h5>
                    <p className="text-xs text-slate-400">Liscard Road, Wallasey · 16 High-Speed Multipumps &amp; 8 EV Ports</p>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
                    Open Now
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* MARTAND MART SECTION */}
      <section id="mart" className="py-24 bg-[#0A0B0E]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div className="max-w-2xl space-y-4">
              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-[#E63946] uppercase">
                <ShoppingBasket className="w-4 h-4" />
                Inside The Mart
              </span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">EVERYTHING YOU NEED, ONE STOP</h2>
              <p className="text-slate-400 text-base">
                More than a petrol station shop — Martand Mart is a full neighbourhood store on Liscard Road. From fresh produce to clothing, stocked daily and open around the clock.
              </p>
            </div>
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold shrink-0" data-testid="mart-open-badge">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Open 24/7
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {MART_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <div
                  key={cat.id}
                  className="rounded-2xl bg-[#12141C] border border-[#252836] p-7 hover:border-[#E63946]/60 transition-all group"
                  data-testid={`mart-category-${cat.id}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${cat.accentBg} ${cat.accent} group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`px-3 py-1 rounded-full bg-[#1A1D29] border border-[#252836] text-[10px] font-bold tracking-wider uppercase ${cat.accent}`}>
                      {cat.tag}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-[#E63946] transition-colors">{cat.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">{cat.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 rounded-2xl overflow-hidden border border-[#252836] bg-[#12141C]">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <img 
                src={IMAGES.martInterior}
                alt="Inside Martand Mart"
                className="w-full h-64 lg:h-full object-cover"
              />
              <div className="p-8 flex flex-col justify-center space-y-4">
                <h3 className="text-xl font-bold">Fresh In, Every Morning</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Our butcher counter, fruit and veg displays, and hot food station are restocked daily before 7am. Meal deals run all day, and the clothing corner refreshes with every season.
                </p>
                <div className="grid grid-cols-3 gap-4 pt-2">
                  <div>
                    <div className="text-xl font-black text-white">6</div>
                    <div className="text-[10px] text-slate-400 font-medium tracking-wider uppercase mt-1">Departments</div>
                  </div>
                  <div>
                    <div className="text-xl font-black text-[#E63946]">Daily</div>
                    <div className="text-[10px] text-slate-400 font-medium tracking-wider uppercase mt-1">Fresh Deliveries</div>
                  </div>
                  <div>
                    <div className="text-xl font-black text-emerald-400">24/7</div>
                    <div className="text-[10px] text-slate-400 font-medium tracking-wider uppercase mt-1">Always Open</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATION FINDER SECTION */}
      <section id="stations" className="py-24 bg-[#0D0E14] border-t border-b border-[#252836]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <span className="text-xs font-bold tracking-[0.2em] text-[#E63946] uppercase">Nationwide Network</span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mt-2">FIND YOUR NEAREST MARTAND HUB</h2>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input 
                  type="text"
                  placeholder="Search station or area..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2.5 rounded-xl bg-[#12141C] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none w-64 transition-all"
                  data-testid="station-search-input"
                />
              </div>
              <button 
                onClick={() => setLocatorModalOpen(true)}
                className="px-5 py-2.5 rounded-xl bg-[#E63946] text-white text-xs font-bold tracking-wider uppercase shadow-lg shadow-[#E63946]/20"
                data-testid="open-map-modal-btn"
              >
                Interactive Map
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredStations.map((station) => (
              <div 
                key={station.id}
                className="rounded-2xl bg-[#12141C] border border-[#252836] p-6 flex flex-col justify-between hover:border-[#E63946]/60 transition-all group"
                data-testid={`station-card-${station.id}`}
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[11px] font-semibold">
                      {station.status}
                    </span>
                    <span className="text-xs font-mono text-slate-400">{station.distance}</span>
                  </div>

                  <h3 className="text-lg font-bold mb-1 group-hover:text-[#E63946] transition-colors">{station.name}</h3>
                  <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-6">
                    <MapPin className="w-3.5 h-3.5 text-[#E63946]" />
                    <span>{station.address}</span>
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-6 p-3 rounded-xl bg-[#1A1D29] border border-[#252836] text-xs">
                    <div>
                      <span className="text-slate-400 block text-[10px]">Pumps</span>
                      <span className="font-bold text-white">{station.pumps} Bays</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">EV Chargers</span>
                      <span className="font-bold text-emerald-400">{station.evChargers} Ports (350kW)</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {station.amenities.map((amenity, i) => (
                      <span key={i} className="px-2.5 py-1 rounded-md bg-[#0A0B0E] border border-[#252836] text-[10px] font-medium text-slate-300">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>

                <button 
                  onClick={() => {
                    toast.success(`Directions loaded for ${station.name}`, {
                      description: `Coordinates: ${station.lat}, ${station.lng}`
                    });
                  }}
                  className="w-full py-3 rounded-xl bg-[#1A1D29] hover:bg-[#E63946] text-white text-xs font-bold tracking-wider uppercase transition-all flex items-center justify-center gap-2"
                  data-testid={`navigate-station-${station.id}`}
                >
                  <Navigation className="w-3.5 h-3.5" />
                  <span>Get Directions</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUSTAINABILITY & GREEN ENERGY */}
      <section id="sustainability" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.neon} 
            alt="Neon canopy" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0E] via-[#0A0B0E]/90 to-[#0A0B0E]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold tracking-[0.2em] text-emerald-400 uppercase">Roadmap to Net Zero</span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">FUELING TODAY. PROTECTING TOMORROW.</h2>
              <p className="text-slate-300 leading-relaxed">
                Martand Petrol is committed to pioneering sustainable mobility. Our forecourts are powered by integrated solar canopy arrays, rainwater harvesting, and 100% LED architectural illumination.
              </p>

              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#12141C]/80 border border-[#252836]">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-sm">Solar Rooftop Canopies</h4>
                    <p className="text-xs text-slate-400 mt-0.5">Every station generates 45% of its daily operational electricity from overhead PV solar glass.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#12141C]/80 border border-[#252836]">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-sm">Carbon Offset Program</h4>
                    <p className="text-xs text-slate-400 mt-0.5">We plant 2 trees for every 100 liters of conventional fuel dispensed across our network.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl bg-[#12141C]/90 backdrop-blur-xl border border-[#252836] p-8 space-y-6 shadow-2xl">
                <h3 className="text-xl font-bold">Network Sustainability Metrics</h3>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-1">
                      <span className="text-slate-300">Renewable Energy Utilization</span>
                      <span className="text-emerald-400 font-mono">68%</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[#1A1D29] overflow-hidden">
                      <div className="h-full bg-emerald-400 rounded-full" style={{ width: "68%" }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-1">
                      <span className="text-slate-300">Water Recycling Efficiency</span>
                      <span className="text-cyan-400 font-mono">92%</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[#1A1D29] overflow-hidden">
                      <div className="h-full bg-cyan-400 rounded-full" style={{ width: "92%" }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-1">
                      <span className="text-slate-300">EV Hyper-Charger Green Power</span>
                      <span className="text-amber-400 font-mono">100%</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[#1A1D29] overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: "100%" }} />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#252836] flex items-center justify-between text-xs">
                  <span className="text-slate-400">Certified by Global Eco-Mobility Index</span>
                  <span className="font-bold text-emerald-400">A+ Rating</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CORPORATE FLEET SECTION */}
      <section id="fleet" className="py-24 bg-[#0D0E14] border-t border-[#252836]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold tracking-[0.2em] text-[#E63946] uppercase">B2B Mobility Solutions</span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">MARTAND FLEET PASS &amp; TELEMATICS</h2>
              <p className="text-slate-300 leading-relaxed">
                Empower your corporate logistics with centralized fuel management, RFID smart cards, real-time mileage tracking, and pre-approved monthly credit lines.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-[#12141C] border border-[#252836]">
                  <Car className="w-6 h-6 text-[#E63946] mb-2" />
                  <h4 className="font-bold text-sm">Smart RFID Cards</h4>
                  <p className="text-xs text-slate-400 mt-1">Instant cashless authorization per vehicle.</p>
                </div>
                <div className="p-4 rounded-xl bg-[#12141C] border border-[#252836]">
                  <Building2 className="w-6 h-6 text-[#E63946] mb-2" />
                  <h4 className="font-bold text-sm">GST Input Tax Credit</h4>
                  <p className="text-xs text-slate-400 mt-1">Automated e-invoicing and tax filing support.</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl bg-[#12141C] border border-[#252836] p-8 shadow-2xl">
                <h3 className="text-xl font-bold mb-2">Request Fleet Account</h3>
                <p className="text-xs text-slate-400 mb-6">Fill out your details for instant corporate onboarding.</p>

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Full Name</label>
                      <input 
                        type="text"
                        required
                        placeholder="Rajesh Sharma"
                        value={formData.fullName}
                        onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                        data-testid="fleet-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Company Name</label>
                      <input 
                        type="text"
                        placeholder="Logistics Corp"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                        data-testid="fleet-company-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Business Email</label>
                      <input 
                        type="email"
                        required
                        placeholder="rajesh@logistics.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                        data-testid="fleet-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Phone Number</label>
                      <input 
                        type="tel"
                        required
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                        data-testid="fleet-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Fleet Size</label>
                    <select 
                      value={formData.fleetSize}
                      onChange={(e) => setFormData({...formData, fleetSize: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                      data-testid="fleet-size-select"
                    >
                      <option>5-10 vehicles</option>
                      <option>10-50 vehicles</option>
                      <option>50-200 vehicles</option>
                      <option>200+ vehicles</option>
                    </select>
                  </div>

                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#c52833] text-white text-xs font-bold tracking-wider uppercase shadow-[0_4px_20px_rgba(230,57,70,0.3)] hover:opacity-95 transition-all flex items-center justify-center gap-2"
                    data-testid="fleet-submit-btn"
                  >
                    {isSubmitting ? "Submitting Request..." : "Request Fleet Account"}
                  </button>
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CAREERS / VACANCIES SECTION */}
      <section id="careers" className="py-24 bg-[#0A0B0E] border-t border-[#252836]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div className="max-w-2xl space-y-4">
              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-[#E63946] uppercase">
                <Briefcase className="w-4 h-4" />
                We Are Hiring
              </span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">JOIN THE MARTAND TEAM</h2>
              <p className="text-slate-400 text-base">
                33 open positions across our Liscard Road mart and petrol pump. Competitive pay, staff discounts, and full training provided. Apply online in under two minutes.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold" data-testid="careers-open-count">
                <Users className="w-3.5 h-3.5" />
                33 Open Roles
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VACANCIES.map((job) => {
              const Icon = job.icon;
              return (
                <div
                  key={job.id}
                  className="rounded-2xl bg-[#12141C] border border-[#252836] p-7 flex flex-col justify-between hover:border-[#E63946]/60 transition-all group"
                  data-testid={`vacancy-card-${job.id}`}
                >
                  <div>
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${job.accentBg} ${job.accent} group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`px-3 py-1 rounded-full bg-[#1A1D29] border border-[#252836] text-xs font-bold font-mono ${job.accent}`} data-testid={`vacancy-openings-${job.id}`}>
                        {job.openings} Openings
                      </span>
                    </div>
                    <h3 className="text-lg font-bold mb-1 group-hover:text-[#E63946] transition-colors">{job.title}</h3>
                    <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 block mb-3">{job.type}</span>
                    <p className="text-xs text-slate-400 leading-relaxed mb-6">{job.description}</p>
                  </div>
                  <button
                    onClick={() => openApplyModal(job.title)}
                    className="w-full py-3 rounded-xl bg-[#1A1D29] hover:bg-[#E63946] text-white text-xs font-bold tracking-wider uppercase transition-all flex items-center justify-center gap-2"
                    data-testid={`apply-btn-${job.id}`}
                  >
                    <span>Apply Now</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-[#12141C] border border-[#252836] flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#E63946]/10 flex items-center justify-center text-[#E63946] shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <p className="text-xs text-slate-400">
                  All roles are based at <span className="text-white font-semibold">Martand Mart &amp; Petrol Pump, Liscard Road, Wallasey, CH44 9AD</span>. Walk-in candidates welcome — ask for the duty manager.
                </p>
              </div>
              <button
                onClick={() => openApplyModal("")}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#E63946] to-[#c52833] text-white text-xs font-bold tracking-wider uppercase shadow-[0_4px_20px_rgba(230,57,70,0.3)] shrink-0"
                data-testid="careers-general-apply-btn"
              >
                Apply Online
              </button>
            </div>

            <div className="p-6 rounded-2xl bg-[#12141C] border border-[#252836]" data-testid="hr-contact-card">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#E63946]/10 flex items-center justify-center text-[#E63946] shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-sm text-white">Mr Robin Russell</h4>
                  <p className="text-[10px] font-semibold tracking-wider uppercase text-[#E63946] mb-2">CEO &amp; Senior HR Manager</p>
                  <div className="space-y-1 text-xs text-slate-300">
                    <a href="mailto:HR@martandpetrol.com" className="block hover:text-white transition-colors" data-testid="hr-contact-email">HR@martandpetrol.com</a>
                    <a href="tel:+447446933478" className="block hover:text-white transition-colors" data-testid="hr-contact-phone-1">+44 7446 933478</a>
                    <a href="tel:+447537155609" className="block hover:text-white transition-colors" data-testid="hr-contact-phone-2">+44 7537 155609</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact" className="py-24 bg-[#0D0E14] border-t border-[#252836]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14 space-y-4">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-[#E63946] uppercase">
              <Phone className="w-4 h-4" />
              Get In Touch
            </span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight">CONTACT MARTAND</h2>
            <p className="text-slate-400 text-base">
              Questions about fuel, the mart, careers, or fleet accounts? Call, email, or send us a message — we reply fast.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5 space-y-4">
              <div className="p-6 rounded-2xl bg-[#12141C] border border-[#252836]" data-testid="contact-person-card">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E63946]/10 border border-[#E63946]/20 flex items-center justify-center text-[#E63946]">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-white">Mr Robin Russell</h4>
                    <p className="text-[10px] font-bold tracking-wider uppercase text-[#E63946]">CEO &amp; Senior HR Manager</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <a href="mailto:HR@martandpetrol.com" className="flex items-center gap-3 p-3 rounded-xl bg-[#1A1D29] border border-[#252836] hover:border-[#E63946]/50 transition-all text-xs text-slate-200" data-testid="contact-email-link">
                    <span className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 shrink-0">@</span>
                    HR@martandpetrol.com
                  </a>
                  <a href="tel:+447446933478" className="flex items-center gap-3 p-3 rounded-xl bg-[#1A1D29] border border-[#252836] hover:border-[#E63946]/50 transition-all text-xs text-slate-200" data-testid="contact-phone-link-1">
                    <span className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 shrink-0"><Phone className="w-4 h-4" /></span>
                    +44 7446 933478
                  </a>
                  <a href="tel:+447537155609" className="flex items-center gap-3 p-3 rounded-xl bg-[#1A1D29] border border-[#252836] hover:border-[#E63946]/50 transition-all text-xs text-slate-200" data-testid="contact-phone-link-2">
                    <span className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400 shrink-0"><Phone className="w-4 h-4" /></span>
                    +44 7537 155609
                  </a>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-[#12141C] border border-[#252836]" data-testid="contact-address-card">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white mb-1">Visit Us</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Martand Mart &amp; Petrol Pump<br />
                      Liscard Road, Wallasey<br />
                      CH44 9AD, United Kingdom
                    </p>
                    <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] font-semibold">
                      <Clock className="w-3 h-3" />
                      Open 24/7 — Forecourt &amp; Mart
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl bg-[#12141C] border border-[#252836] p-8 shadow-2xl h-full">
                <h3 className="text-xl font-bold mb-2">Send Us A Message</h3>
                <p className="text-xs text-slate-400 mb-6">We usually respond within a few hours.</p>

                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Full Name</label>
                      <input 
                        type="text"
                        required
                        placeholder="John Smith"
                        value={contactForm.fullName}
                        onChange={(e) => setContactForm({...contactForm, fullName: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Email</label>
                      <input 
                        type="email"
                        required
                        placeholder="john@email.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Phone (optional)</label>
                      <input 
                        type="tel"
                        placeholder="+44 7911 123456"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Subject</label>
                      <select 
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                        data-testid="contact-subject-select"
                      >
                        <option>General Enquiry</option>
                        <option>Fuel & Pricing</option>
                        <option>Mart & Store</option>
                        <option>Careers & Jobs</option>
                        <option>Fleet Program</option>
                        <option>Feedback / Complaint</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Message</label>
                    <textarea 
                      rows={5}
                      required
                      placeholder="How can we help?"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button 
                    type="submit"
                    disabled={isSendingContact}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#c52833] text-white text-xs font-bold tracking-wider uppercase shadow-[0_4px_20px_rgba(230,57,70,0.3)] hover:opacity-95 transition-all flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {isSendingContact ? "Sending Message..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#07080B] border-t border-[#252836] pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-12 pb-12 border-b border-[#252836]">
            
            <div className="space-y-4 md:col-span-1">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E63946] to-[#1D3557] flex items-center justify-center">
                  <Flame className="w-4 h-4 text-white" />
                </div>
                <span className="font-extrabold tracking-wider text-lg">MARTAND</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                The pinnacle of fuel quality, architectural forecourt design, and sustainable mobility solutions.
              </p>
            </div>

            <div>
              <h5 className="font-bold text-sm mb-4 text-white">Fuel &amp; Octane</h5>
              <ul className="space-y-2.5 text-xs text-slate-400">
                <li><a href="#fuels" className="hover:text-white transition-colors">Speed 99 Octane</a></li>
                <li><a href="#fuels" className="hover:text-white transition-colors">Ultra-Clean Diesel</a></li>
                <li><a href="#fuels" className="hover:text-white transition-colors">Martand Gold</a></li>
                <li><a href="#fuels" className="hover:text-white transition-colors">350kW DC Hyper-Charge</a></li>
              </ul>
            </div>

            <div>
              <h5 className="font-bold text-sm mb-4 text-white">Forecourt Hubs</h5>
              <ul className="space-y-2.5 text-xs text-slate-400">
                <li><a href="#stations" className="hover:text-white transition-colors">Liscard Road, Wallasey</a></li>
                <li><a href="#mart" className="hover:text-white transition-colors" data-testid="footer-mart-link">Martand Mart — 24/7 Store</a></li>
                <li><a href="#stations" className="hover:text-white transition-colors">Downtown Terminal</a></li>
                <li><a href="#stations" className="hover:text-white transition-colors">Interactive Station Map</a></li>
              </ul>
            </div>

            <div>
              <h5 className="font-bold text-sm mb-4 text-white">Corporate &amp; Legal</h5>
              <ul className="space-y-2.5 text-xs text-slate-400">
                <li><a href="#fleet" className="hover:text-white transition-colors">Fleet Pass Program</a></li>
                <li><a href="#careers" className="hover:text-white transition-colors" data-testid="footer-careers-link">Careers — We're Hiring</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Safety Standards &amp; ISO</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>

            <div>
              <h5 className="font-bold text-sm mb-4 text-white">Contact Us</h5>
              <ul className="space-y-2.5 text-xs text-slate-400" data-testid="footer-contact-details">
                <li className="text-white font-semibold">Mr Robin Russell</li>
                <li className="text-[10px] uppercase tracking-wider text-[#E63946] font-bold">CEO &amp; Senior HR Manager</li>
                <li><a href="mailto:HR@martandpetrol.com" className="hover:text-white transition-colors">HR@martandpetrol.com</a></li>
                <li><a href="tel:+447446933478" className="hover:text-white transition-colors">+44 7446 933478</a></li>
                <li><a href="tel:+447537155609" className="hover:text-white transition-colors">+44 7537 155609</a></li>
              </ul>
            </div>

          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
            <p>&copy; {new Date().getFullYear()} Martand Petrol &amp; Mobility Hubs Inc. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <span>ISO 9001:2026 Certified</span>
              <span>Green Energy Compliant</span>
            </div>
          </div>
        </div>
      </footer>

      {/* STATION LOCATOR MODAL */}
      <AnimatePresence>
        {locatorModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-x-hidden">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-3xl rounded-2xl bg-[#12141C] border border-[#252836] p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto"
              data-testid="station-locator-modal"
            >
              <button 
                onClick={() => setLocatorModalOpen(false)}
                className="absolute top-6 right-6 p-2 rounded-lg bg-[#1A1D29] text-slate-400 hover:text-white"
                data-testid="close-locator-modal"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#E63946]/10 flex items-center justify-center text-[#E63946]">
                  <Navigation className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Martand Station Locator</h3>
                  <p className="text-xs text-slate-400">Select a hub for real-time bay availability and pricing</p>
                </div>
              </div>

              <div className="space-y-4">
                {STATIONS.map((station) => (
                  <div key={station.id} className="p-4 rounded-xl bg-[#1A1D29] border border-[#252836] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-sm text-white">{station.name}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-semibold">{station.status}</span>
                      </div>
                      <p className="text-xs text-slate-400 mb-2">{station.address}</p>
                      <div className="flex items-center gap-4 text-[11px] text-slate-300">
                        <span>⛽ {station.pumps} Pumps</span>
                        <span>⚡ {station.evChargers} EV Ports (350kW)</span>
                        <span className="text-amber-400 font-mono">{station.distance}</span>
                      </div>
                    </div>
                    <button 
                      onClick={() => {
                        toast.success(`Route plotted to ${station.name}`);
                        setLocatorModalOpen(false);
                      }}
                      className="px-4 py-2.5 rounded-lg bg-[#E63946] text-white text-xs font-bold uppercase tracking-wider shrink-0"
                      data-testid={`modal-route-btn-${station.id}`}
                    >
                      Route Here
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ENQUIRY MODAL */}
      <AnimatePresence>
        {enquiryModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-x-hidden">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="box-border w-full max-w-xl min-w-0 rounded-2xl bg-[#12141C] border border-[#252836] p-6 shadow-2xl relative max-h-[90vh] overflow-x-hidden overflow-y-auto"
              data-testid="enquiry-modal"
            >
              <button 
                onClick={() => setEnquiryModalOpen(false)}
                className="absolute top-6 right-6 p-2 rounded-lg bg-[#1A1D29] text-slate-400 hover:text-white"
                data-testid="close-enquiry-modal"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#E63946]/10 flex items-center justify-center text-[#E63946]">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Corporate Fleet Pass</h3>
                  <p className="text-xs text-slate-400">Apply for business credit lines and RFID access</p>
                </div>
              </div>

              <form onSubmit={handleFormSubmit} className="min-w-0 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Full Name</label>
                  <input 
                    type="text"
                    required
                    placeholder="Rajesh Sharma"
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                    data-testid="modal-fleet-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Company Name</label>
                  <input 
                    type="text"
                    placeholder="Logistics Corp"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                    data-testid="modal-fleet-company"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Business Email</label>
                    <input 
                      type="email"
                      required
                      placeholder="rajesh@logistics.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                      data-testid="modal-fleet-email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Phone Number</label>
                    <input 
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                      data-testid="modal-fleet-phone"
                    />
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#c52833] text-white text-xs font-bold tracking-wider uppercase shadow-[0_4px_20px_rgba(230,57,70,0.3)] hover:opacity-95 transition-all flex items-center justify-center gap-2 mt-4"
                  data-testid="modal-fleet-submit"
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* JOB APPLICATION MODAL */}
      <AnimatePresence>
        {applyModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-x-hidden">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="box-border w-full max-w-xl min-w-0 rounded-2xl bg-[#12141C] border border-[#252836] p-6 shadow-2xl relative max-h-[90vh] overflow-x-hidden overflow-y-auto"
              data-testid="apply-modal"
            >
              <button 
                onClick={() => setApplyModalOpen(false)}
                className="absolute top-6 right-6 p-2 rounded-lg bg-[#1A1D29] text-slate-400 hover:text-white"
                data-testid="close-apply-modal"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#E63946]/10 flex items-center justify-center text-[#E63946]">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Job Application</h3>
                  <p className="text-xs text-slate-400">Martand Mart &amp; Petrol Pump — Liscard Road, Wallasey</p>
                </div>
              </div>

              <form onSubmit={handleApplySubmit} className="min-w-0 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Position Applying For</label>
                  <select 
                    value={applyForm.position}
                    onChange={(e) => setApplyForm({...applyForm, position: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                    data-testid="apply-position-select"
                  >
                    {VACANCIES.map((job) => (
                      <option key={job.id} value={job.title}>{job.title} ({job.openings} openings)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Full Name</label>
                  <input 
                    type="text"
                    required
                    placeholder="John Smith"
                    value={applyForm.fullName}
                    onChange={(e) => setApplyForm({...applyForm, fullName: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                    data-testid="apply-name-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Email</label>
                    <input 
                      type="email"
                      required
                      placeholder="john@email.com"
                      value={applyForm.email}
                      onChange={(e) => setApplyForm({...applyForm, email: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                      data-testid="apply-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Phone Number</label>
                    <input 
                      type="tel"
                      required
                      placeholder="+44 7911 123456"
                      value={applyForm.phone}
                      onChange={(e) => setApplyForm({...applyForm, phone: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                      data-testid="apply-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Experience</label>
                  <select 
                    value={applyForm.experience}
                    onChange={(e) => setApplyForm({...applyForm, experience: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none"
                    data-testid="apply-experience-select"
                  >
                    <option value="">No prior experience</option>
                    <option value="Less than 1 year">Less than 1 year</option>
                    <option value="1-3 years">1-3 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="5+ years">5+ years</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Why should we hire you? (optional)</label>
                  <textarea 
                    rows={3}
                    placeholder="Tell us briefly about yourself..."
                    value={applyForm.message}
                    onChange={(e) => setApplyForm({...applyForm, message: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#1A1D29] border border-[#252836] focus:border-[#E63946] text-xs text-white outline-none resize-none"
                    data-testid="apply-message-input"
                  />
                </div>

                <button 
                  type="submit"
                  disabled={isApplying}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#c52833] text-white text-xs font-bold tracking-wider uppercase shadow-[0_4px_20px_rgba(230,57,70,0.3)] hover:opacity-95 transition-all flex items-center justify-center gap-2 mt-2"
                  data-testid="apply-submit-btn"
                >
                  {isApplying ? "Submitting Application..." : "Submit Application"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
